<template>
  <PlayerPanel
    :open="player.activePanel.value !== null"
    :title="panelTitle"
    @close="player.closePanel()"
  >
    <SettingsPanel
      v-if="player.activePanel.value === PanelType.SETTINGS"
      :player="player"
    />
    <ChaptersList
      v-else-if="player.activePanel.value === PanelType.CHAPTERS"
      :player="player"
    />
    <PlaylistPanel
      v-else-if="player.activePanel.value === PanelType.PLAYLIST"
      :items="player.items.value"
      :current-index="player.currentIndex.value"
      :is-playing="player.isPlaying.value"
      @select="player.goToIndex"
    />
    <TranscriptPanel
      v-else-if="player.activePanel.value === PanelType.TRANSCRIPT"
      :player="player"
      :show-chapters="showChapters"
    />
    <VideoDetailsPanel
      v-else-if="
        player.activePanel.value === PanelType.DETAILS ||
        player.activePanel.value === PanelType.INFO
      "
      :player="player"
    />
    <KeyboardShortcutsPanel
      v-else-if="player.activePanel.value === PanelType.KEYBOARD_SHORTCUTS"
      :player="player"
    />
  </PlayerPanel>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { UseVideoPlayerReturn } from '../../composables/useVideoPlayer'
import { PanelType } from '../../types'
import { useVideoI18n } from '../../i18n'
import PlayerPanel from './PlayerPanel.vue'
import SettingsPanel from './SettingsPanel.vue'
import ChaptersList from './ChaptersList.vue'
import PlaylistPanel from './PlaylistPanel.vue'
import TranscriptPanel from './TranscriptPanel.vue'
import VideoDetailsPanel from './VideoDetailsPanel.vue'
import KeyboardShortcutsPanel from './KeyboardShortcutsPanel.vue'

interface Props {
  player: UseVideoPlayerReturn
  /** The embed's `show_chapters`, which the transcript's chapter headings honour as the bar does. */
  showChapters?: boolean
}

const props = withDefaults(defineProps<Props>(), { showChapters: true })
const { t } = useVideoI18n()

const panelTitleKeys: Record<PanelType, string> = {
  [PanelType.SETTINGS]: 'player.settings.title',
  [PanelType.CHAPTERS]: 'player.controls.chapters',
  [PanelType.PLAYLIST]: 'player.controls.playlist',
  [PanelType.TRANSCRIPT]: 'player.controls.transcript',
  [PanelType.FILES]: 'player.info.files',
  [PanelType.DETAILS]: 'player.info.details',
  [PanelType.INFO]: 'player.controls.info',
  [PanelType.QUALITY]: 'player.controls.quality',
  [PanelType.SUBTITLES]: 'player.controls.subtitles',
  [PanelType.AUDIO_TRACKS]: 'player.settings.audioTrack',
  [PanelType.KEYBOARD_SHORTCUTS]: 'player.settings.shortcuts',
}

const panelTitle = computed(() => {
  const panel = props.player.activePanel.value
  if (!panel) return ''
  const key = panelTitleKeys[panel]
  return key ? t(key) : ''
})
</script>
